import { useEffect, FC, memo, useRef } from 'react';
import type { AnyAction } from '@reduxjs/toolkit';
import { useSelector, useDispatch } from 'react-redux';
import type { AppState, AppDispatch } from '@nextgen-web-framework/core';
import {
  TextLink,
  TextLinkStyle,
  useTranslation,
  Theme,
  ButtonStyles,
  Button,
  ButtonIconPosition,
  IconAnimationClassName,
  useSession,
} from '@nextgen-web-framework/all';
import {
  Address,
  BopisRecipientDetail,
  CheckoutResponse,
  ServiceLocation,
  confirmDeliveryOptionCheckoutSliceReq,
  setPickupDrawerVisibility,
  setPickupContactDrawerVisibility,
  updateDeliveryOptionCheckoutSliceReq,
  FulfillmentRequest,
  ErrorResponse,
  getFulfillmentTypesSliceReq,
  getValueCheck,
  checkoutFulfillmentOptions,
  isRegistrationOrder,
  isProxyOrder,
  isGroupOrder,
  setPlaceOrderDisabled,
  ContactType as AddressContactType,
  InitialCheckoutState,
  setShippingDetailsContainerState,
  getDefaultWarehouseId,
  resetPickupStoreOptions,
  isProxyNotGroupOrder,
} from '@commerce-checkout-webcomponents/sdk';
import { AddressBook, BadAddressAlert } from '@commerce-profile-webcomponents/ui';
import { ExtendedContactType, ValidationType } from '@commerce-profile-webcomponents/sdk';
import {
  IFulfillmentTypesAndStoreMap,
  IkeyStringWithKeyStringObject,
} from '@commerce-checkout-webcomponents/sdk/types';
import { OrderType } from '@commerce-cart-webcomponents/sdk';
import { ContainerStateEnum, DataFlowEnum } from '../common/types/CheckoutType';
import {
  shippingDetailsUseEffect,
  validateFulfillmentTypesAndStoreMap,
  useShippingDetailsReducer,
  transformCheckoutAddressToConfirmedAddress,
  transformCheckoutAddressToSelectedAddress,
  transformCheckoutDataToShippingDetails,
  checkPickupExist,
  dispatchActionType,
} from '../common/helpers/CheckoutShippingHelpers';
import { DataTypesEnums, ShippingTypeEnums, checkoutShippingDetailsActions } from '../common/config/checkoutConstants';
import { stateTextLocalization, stringBooleanUndefinedChecksOutput } from '../common/transformer';
import styles from './CheckoutShippingDetailsContainer.module.scss';
import {
  ICheckoutShippingDetailsContainerProps,
  ContactType,
  IShippingInitialState,
  ICheckoutIsSaved,
} from './types/propTypes';
import CheckoutShippingType from './checkout-shipping-type/CheckoutShippingType';
import CheckoutSelectedAddressCard from './checkout-selected-address-card/CheckoutSelectedAddressCard';
import CheckoutConfirmedAddressDetails from './checkout-confirmed-address-details/CheckoutConfirmedAddressDetails';
import CheckoutAddNewAddress from './checkout-add-new-address/CheckoutAddNewAddress';
import CheckoutPickupDrawer from './checkout-pickup-drawer/CheckoutPickupDrawer';
import CheckoutPickupContactDrawer from './checkout-pickup-contact-drawer/CheckoutPickupContactDrawer';
export let dispatchShippingAction: dispatchActionType;
export let shippingState: IShippingInitialState;

export const CheckoutShippingDetailsContainer: FC<ICheckoutShippingDetailsContainerProps> = (
  props: ICheckoutShippingDetailsContainerProps,
) => {
  const { dataFlow, data } = props;
  const { isAnonymous, session } = useSession();
  const configs = ConfigService.getConfigs();
  const pickupLatamFlow = configs.NEXT_PUBLIC_ENABLE_PICKUP_LATAM_FLOW as boolean;
  const pickupCheckoutLatamFlow = pickupLatamFlow && dataFlow === DataFlowEnum.CHECKOUT;
  const containerStateRef = useRef<boolean>(true);
  const { disabledPlaceOrder } = useSelector((state: AppState): InitialCheckoutState => state.commerceCheckout);
  const checkout: CheckoutResponse = useSelector((state: AppState) => {
    if (dataFlow === DataFlowEnum.CHECKOUT && state?.commerceCheckout?.checkout) {
      return state.commerceCheckout.checkout;
    } else {
      return null;
    }
  });

  const fulfillmentCmsData: IkeyStringWithKeyStringObject = useSelector((state: AppState) => {
    if (state?.commerceCheckout?.fulfillmentCmsContent) {
      return state.commerceCheckout.fulfillmentCmsContent;
    } else {
      return null;
    }
  });

  const fulfillmentTypesAndStoreMap: IFulfillmentTypesAndStoreMap = useSelector(
    (state: AppState) => state.commerceCheckout.fulfillmentTypesAndStoreMap,
  );

  const productEntries = useSelector((state: AppState) => {
    if (dataFlow === DataFlowEnum.CHECKOUT && state?.commerceCheckout?.checkout) {
      return state.commerceCheckout.checkout.entries;
    } else {
      return data?.entries || [];
    }
  });
  const currencyIsoCode = useSelector((state: AppState) => {
    if (dataFlow === DataFlowEnum.CHECKOUT && state?.commerceCheckout?.checkout) {
      return state.commerceCheckout.checkout.currencyIsoCode;
    } else {
      return data?.currencyIsoCode || '';
    }
  });
  const pickupDrawerVisibility: boolean = useSelector(
    (state: AppState) => state?.commerceCheckout?.pickupDrawerVisibility,
  );
  const pickupContactDrawerVisibility: boolean = useSelector(
    (state: AppState) => state?.commerceCheckout?.pickupContactDrawerVisibility,
  );
  const shippingDetailsContainerState: string | undefined = useSelector(
    (state: AppState) => state?.commerceCheckout?.shippingDetailsContainerState,
  );
  const [state, dispatchShippingStateAction] = useShippingDetailsReducer();
  dispatchShippingAction = dispatchShippingStateAction;
  const t = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const shippingDetailsUpdating: boolean =
    useSelector(
      (state: AppState) => state?.commerceCheckout?.loadingState?.shippingDetailsLoading?.isLoading || false,
    ) || state.shippingDetailsUpdating;

  useEffect(() => {
    if (dataFlow === DataFlowEnum.CHECKOUT && checkout && containerStateRef.current) {
      dispatchShippingStateAction({
        type: checkoutShippingDetailsActions.SET_SHIPPING_DETAILS_DATA,
        payload: transformCheckoutDataToShippingDetails(checkout),
      });
      const checkoutBopisDetails = checkout?.fulfillment?.bopisRecipientDetail;
      if (
        pickupLatamFlow &&
        checkout?.fulfillment?.id != checkoutFulfillmentOptions.HOMEDELIVERY &&
        (!checkoutBopisDetails?.email || !checkoutBopisDetails?.mobileNo || !checkoutBopisDetails?.name)
      ) {
        dispatch(setPickupContactDrawerVisibility(true));
      }
    } else if (dataFlow != DataFlowEnum.CHECKOUT) {
      dispatchShippingStateAction({
        type: checkoutShippingDetailsActions.SET_SHIPPING_DETAILS_DATA,
        payload: { ...data, isPickupAddressSelectable: true, isDeliveryAddressSelectable: true },
      });
    }
    shippingState = state;
  }, [checkout, data]);

  useEffect(() => {
    dispatch(getFulfillmentTypesSliceReq() as unknown as AnyAction);
  }, []);

  useEffect(() => {
    if (state.displayAddressSelection) {
      containerStateRef.current = false;
    }
  }, [state.displayAddressSelection]);

  useEffect(() => {
    if (shippingDetailsContainerState != ContainerStateEnum.EXPANDED) {
      shippingDetailsUseEffect(
        state.shippingDetailsData,
        dispatchShippingStateAction,
        checkout?.fulfillment?.isRestricted || false,
        pickupContactDrawerVisibility,
      );
    }
  }, [state.shippingDetailsData, shippingDetailsContainerState]);

  useEffect(() => {
    validateFulfillmentTypesAndStoreMap(state.shippingType, fulfillmentTypesAndStoreMap, dispatchShippingStateAction);
  }, [fulfillmentTypesAndStoreMap]);

  const actionsAfterSavingDeliveryPickupAddress = (isSaved?: boolean) => {
    dispatch(setPlaceOrderDisabled(false));
    dispatch(setShippingDetailsContainerState(ContainerStateEnum.FILLED) as unknown as AnyAction);
    if (pickupDrawerVisibility) {
      dispatch(setPickupDrawerVisibility(false));
      dispatch(resetPickupStoreOptions());
    }
    if (state.displayAddressSelection) {
      dispatchShippingStateAction({
        type: checkoutShippingDetailsActions.SET_DISPLAY_ADDRESS_SELECTION,
        payload: false,
      });
    }
    if (state.displayAddressBook) {
      dispatchShippingStateAction({
        type: checkoutShippingDetailsActions.SET_DISPLAY_ADDRESS_BOOK,
        payload: false,
      });
    }
    dispatchShippingStateAction({
      type: checkoutShippingDetailsActions.SET_DISPLAY_CONFIRMED_ADDRESS,
      payload: true,
    });
    if (typeof isSaved == 'boolean') {
      dispatchShippingStateAction({
        type: checkoutShippingDetailsActions.SET_IS_ADDRESS_SAVED_FLAG,
        payload: isSaved,
      });
    }
    if (pickupContactDrawerVisibility) {
      dispatch(setPickupContactDrawerVisibility(false));
    }
  };

  const isShippingAddress = (): boolean => {
    let isShipping = true;
    isShipping = checkout?.fulfillment?.address?.contactType
      ? checkout?.fulfillment?.address?.contactType == AddressContactType.Shipping
      : true;
    return isShipping;
  };

  const confirmButtonCta = (isDisabled?: boolean) => (
    <div className={styles['checkout-confirm-address-button-container']}>
      <Button
        title={
          shippingDetailsUpdating && !state.displayAddressBook && !pickupDrawerVisibility
            ? undefined
            : t('checkout.CheckoutShippingAddressContainer.confirmCTA')
        }
        buttonStyle={ButtonStyles.SOLID}
        onClick={() => addressSelectionConfirmActionHandler()}
        disabled={isDisabled}
        id={styles['checkout-confirm-address-button']}
        {...(shippingDetailsUpdating &&
          !state.displayAddressBook &&
          !pickupDrawerVisibility && {
            iconPosition: ButtonIconPosition.LEFT,
            icon: {
              iconName: 'spinner-third',
              iconAnimationClassName: IconAnimationClassName.SPIN,
            },
          })}
      />
    </div>
  );

  const updateFulfillmentSuccessHandler = (response: CheckoutResponse & ErrorResponse, isSaved?: boolean) => {
    dispatchShippingStateAction({
      type: checkoutShippingDetailsActions.SET_SHIPPING_DETAILS_UPDATING,
      payload: false,
    });
    if (!response?.code) {
      actionsAfterSavingDeliveryPickupAddress(isSaved);
    }
  };

  const saveShippingDetails = async (addressObj: (Address & ContactType & ICheckoutIsSaved) | ExtendedContactType) => {
    containerStateRef.current = true;
    const {
      city,
      contactId,
      contactNickname,
      country,
      state,
      recipientFirstName,
      recipientLastName,
      addrLineOne,
      addrLineTwo,
      postalCode,
      phone,
      email,
      deliveryInstructions,
      ownerId,
      district,
      subDistrict,
      province,
      isSaved,
      validationStatus,
      daneCode,
    } = addressObj;
    if (dataFlow == DataFlowEnum.CHECKOUT && checkout?.checkoutId) {
      dispatchShippingStateAction({
        type: checkoutShippingDetailsActions.SET_SHIPPING_DETAILS_UPDATING,
        payload: true,
      });
      const defaultWarehouseId = await getDefaultWarehouseId(postalCode);
      const payload: FulfillmentRequest = {
        fulfillment: {
          id: checkoutFulfillmentOptions.HOMEDELIVERY,
          address: {
            ...checkout?.fulfillment?.address,
            city: getValueCheck(city, DataTypesEnums.TYPE_STRING),
            contactId,
            contactNickname,
            country,
            state: getValueCheck(state, DataTypesEnums.TYPE_STRING),
            recipientFirstName,
            recipientLastName,
            addrLineOne: getValueCheck(addrLineOne, DataTypesEnums.TYPE_STRING),
            addrLineTwo,
            postalCode: getValueCheck(postalCode, DataTypesEnums.TYPE_STRING),
            phone,
            email,
            deliveryInstructions,
            ownerId,
            district,
            subDistrict,
            province,
            validationStatus,
            daneCode,
          },
          warehouseId: defaultWarehouseId,
        },
        version: checkout.version,
      };

      if (checkout?.fulfillment?.requestId) {
        dispatch(
          updateDeliveryOptionCheckoutSliceReq({
            checkoutId: checkout?.checkoutId,
            fulfillmentId: checkout?.fulfillment?.requestId,
            payload,
          }) as unknown as AnyAction,
        ).then((response: CheckoutResponse & ErrorResponse) => {
          updateFulfillmentSuccessHandler(response, isSaved);
        });
      } else {
        dispatch(
          confirmDeliveryOptionCheckoutSliceReq({
            checkoutId: checkout.checkoutId,
            payload,
          }) as unknown as AnyAction,
        ).then((response: CheckoutResponse & ErrorResponse) => {
          updateFulfillmentSuccessHandler(response, isSaved);
        });
      }
    } else {
      actionsAfterSavingDeliveryPickupAddress(isSaved);
      data?.onDeliveryAddressConfirmClick && data?.onDeliveryAddressConfirmClick(addressObj);
    }
    return null;
  };

  const changeCtaHandler = () => {
    if (dataFlow == DataFlowEnum.CHECKOUT) {
      dispatch(setPlaceOrderDisabled(true));
      dispatch(setShippingDetailsContainerState(ContainerStateEnum.EXPANDED) as unknown as AnyAction);
    }
    dispatchShippingStateAction({
      type: checkoutShippingDetailsActions.SET_DISPLAY_CONFIRMED_ADDRESS,
      payload: false,
    });
    dispatchShippingStateAction({
      type: checkoutShippingDetailsActions.SET_DISPLAY_ADDRESS_SELECTION,
      payload: true,
    });
  };

  const confirmPickupStoreHandler = async (
    bopisRecipientDetail: BopisRecipientDetail,
    selectedStoreOptionName: string,
    selectedServiceLocation: ServiceLocation,
  ) => {
    containerStateRef.current = true;
    if (dataFlow == DataFlowEnum.CHECKOUT) {
      dispatchShippingStateAction({
        type: checkoutShippingDetailsActions.SET_SHIPPING_DETAILS_UPDATING,
        payload: true,
      });
      const isThirdPartyPickupStore = selectedStoreOptionName != checkoutFulfillmentOptions.BOPIS;
      const warehouseId = isThirdPartyPickupStore
        ? await getDefaultWarehouseId(selectedServiceLocation?.address?.postCode)
        : undefined;
      const fulfillmentReqData = {
        fulfillment: {
          id: selectedStoreOptionName,
          serviceLocation: selectedServiceLocation,
          bopisRecipientDetail,
          ...(isThirdPartyPickupStore && { warehouseId }),
        },
        version: checkout?.version,
      };
      if (checkout?.fulfillment?.requestId) {
        dispatch(
          updateDeliveryOptionCheckoutSliceReq({
            checkoutId: checkout?.checkoutId,
            fulfillmentId: checkout?.fulfillment?.requestId,
            payload: fulfillmentReqData,
          }) as unknown as AnyAction,
        ).then((response: CheckoutResponse & ErrorResponse) => {
          updateFulfillmentSuccessHandler(response);
        });
      } else {
        dispatch(
          confirmDeliveryOptionCheckoutSliceReq({
            checkoutId: checkout?.checkoutId,
            payload: fulfillmentReqData,
          }) as unknown as AnyAction,
        ).then((response: CheckoutResponse & ErrorResponse) => {
          updateFulfillmentSuccessHandler(response);
        });
      }
    } else {
      dispatch(setPickupDrawerVisibility(false));
      dispatch(setPickupContactDrawerVisibility(false));
      dispatch(resetPickupStoreOptions());
      dispatch(setShippingDetailsContainerState(ContainerStateEnum.FILLED) as unknown as AnyAction);
      data?.onPickupAddressConfirmClick &&
        selectedServiceLocation &&
        selectedStoreOptionName &&
        data?.onPickupAddressConfirmClick(selectedServiceLocation, selectedStoreOptionName, bopisRecipientDetail);
    }
  };

  const getSelectedPickupStoreIcon = () => {
    const fulfillmentId =
      dataFlow == DataFlowEnum.CHECKOUT
        ? state.fulfillmentTypeForpickup || checkout?.fulfillment?.id
        : data?.fulfillmentId;
    return (fulfillmentCmsData && fulfillmentId && fulfillmentCmsData?.[fulfillmentId]?.iconUrl) || '';
  };

  const shippingTypeVisibleCheck = () =>
    fulfillmentTypesAndStoreMap?.delivery?.isExist && checkPickupExist(fulfillmentTypesAndStoreMap);

  const addNewAddressVisibleCheck = () =>
    (state.shippingType == ShippingTypeEnums.DELIVERY &&
      !state.isDeliveryAddressAvailable &&
      (checkout?.checkoutId || !isAnonymous())) ||
    (state.shippingType == ShippingTypeEnums.PICKUP && !state.isPickupAddressAvailable);

  const selectedAddressWithConfirmVisibleCheck = () =>
    (state.shippingType == ShippingTypeEnums.DELIVERY && state.isDeliveryAddressAvailable) ||
    (state.shippingType == ShippingTypeEnums.PICKUP && state.isPickupAddressAvailable);

  const addressSelectionConfirmActionHandler = () => {
    containerStateRef.current = true;
    if (dataFlow != DataFlowEnum.CHECKOUT) {
      dispatch(setShippingDetailsContainerState(ContainerStateEnum.FILLED) as unknown as AnyAction);
      data?.onAddressSelectionConfirmClick && data?.onAddressSelectionConfirmClick(state.shippingType);
    } else {
      if (state.shippingType == ShippingTypeEnums.DELIVERY) {
        saveShippingDetails(state.deliveryAddress);
      } else {
        if (!pickupCheckoutLatamFlow) {
          checkout?.fulfillment?.bopisRecipientDetail &&
            checkout?.fulfillment?.serviceLocation &&
            confirmPickupStoreHandler(
              checkout?.fulfillment?.bopisRecipientDetail,
              checkout?.fulfillment?.id,
              checkout?.fulfillment?.serviceLocation,
            );
        } else {
          confirmPickupStoreHandler(
            state.shippingDetailsData?.bopisDetails,
            state.fulfillmentTypeForpickup || checkout?.fulfillment?.id,
            state.storePickupAddress,
          );
        }
      }
    }
  };

  const getConfirmCtaDisabledState = () =>
    (state.shippingType == ShippingTypeEnums.DELIVERY && state?.softFilters?.delivery?.isVisible) ||
    (state.shippingType == ShippingTypeEnums.PICKUP && state?.softFilters?.pickup?.isVisible) ||
    (shippingDetailsUpdating && !pickupDrawerVisibility && !state.displayAddressBook);

  const hideSelectedAddressEdit = (): boolean => {
    let hideEdit = false;
    if (
      state.shippingType == ShippingTypeEnums.DELIVERY &&
      (data?.isDeliveryAddressEditable ||
        (dataFlow == DataFlowEnum.CHECKOUT && (isRegistrationOrder(checkout) || !isShippingAddress())))
    ) {
      hideEdit = true;
    }
    return hideEdit;
  };

  const shippingTypeChangeHandler = (shippingType: string) => {
    if (shippingType == ShippingTypeEnums.DELIVERY && !state.isDeliveryAddressAvailable) {
      dispatchShippingStateAction({
        type: checkoutShippingDetailsActions.SET_DISPLAY_ADD_NEW_ADDRESS_FOR_DELIVERY,
        payload: true,
      });
      isAnonymous() &&
        dataFlow != DataFlowEnum.CHECKOUT &&
        data?.onAddressSelectionConfirmClick &&
        data?.onAddressSelectionConfirmClick(shippingType);
    } else if (shippingType == ShippingTypeEnums.PICKUP) {
      dispatchShippingStateAction({
        type: checkoutShippingDetailsActions.SET_DISPLAY_ADD_NEW_ADDRESS_FOR_PICKUP,
        payload: true,
      });
      if (!state.isPickupAddressAvailable && !pickupCheckoutLatamFlow) {
        dispatch(setPickupDrawerVisibility(true));
      }
    }
    dispatchShippingStateAction({
      type: checkoutShippingDetailsActions.SET_SHIPPING_TYPE,
      payload: shippingType,
    });
  };

  const getProfileName = () => {
    if (state.shippingType == ShippingTypeEnums.PICKUP) {
      return undefined;
    }
    if (
      session?.id &&
      state.deliveryAddress?.ownerId &&
      session.id != state.deliveryAddress?.ownerId &&
      session.aboNum != state.deliveryAddress?.ownerId &&
      ((dataFlow != DataFlowEnum.CHECKOUT && data?.cartData?.orderType == OrderType.Regular) ||
        (dataFlow == DataFlowEnum.CHECKOUT && !isProxyOrder(checkout) && !isGroupOrder(checkout)))
    ) {
      return t('checkout.CheckoutShippingDetailsContainer.downlineAddressText');
    }
    return state.shippingDetailsData?.downlineAddressListMap?.[state.deliveryAddress?.ownerId];
  };

  const isAddressSavedWithContactId = () => {
    if (state.isAddressSavedFlag && state.deliveryAddress?.contactId) {
      return true;
    }
    return false;
  };

  const invalidAddressAlert = () => {
    if (
      state.deliveryAddress?.validationStatus == ValidationType.NotValidated &&
      state.shippingType == ShippingTypeEnums.DELIVERY &&
      !isProxyOrder(checkout) &&
      !isGroupOrder(checkout) &&
      !getProfileName()
    ) {
      return (
        <BadAddressAlert
          address={state.deliveryAddress}
          showChangeCTA={true}
          onConfirmAddress={(addressObj: (Address & ContactType) | ExtendedContactType) => {
            saveShippingDetails(addressObj);
          }}
        />
      );
    }
    return null;
  };

  return (
    <div className={`${styles['shipping-address-container']}`}>
      <h3
        className={`${!shippingTypeVisibleCheck() && styles['shipping-address-container-title']} ${
          dataFlow != DataFlowEnum.CHECKOUT && styles['shipping-address-location-title']
        }`}
      >
        {t('checkout.CheckoutShippingDetailsContainer.headingText')}
        {state.displayConfirmedAddress && !state.displayAddressSelection && (
          <div className={`${styles['change-cta-container']}`}>
            <TextLink
              id={styles['change-cta']}
              data-testid="change-cta"
              text={t('checkout.CheckoutShippingDetailsContainer.headingActionText')}
              textLinkStyle={TextLinkStyle.UNDERLINE}
              theme={Theme.PURPLE}
              onClick={() => changeCtaHandler()}
              scroll={false}
            />
          </div>
        )}
      </h3>
      {state.displayAddressSelection && (
        <>
          {shippingTypeVisibleCheck() && (
            <CheckoutShippingType
              shippingType={state.shippingType}
              onChangeCB={shippingTypeChangeHandler}
              isDeliveryDisabled={dataFlow != DataFlowEnum.CHECKOUT && data?.typeDeliveryDisabled}
              isPickupDisabled={dataFlow != DataFlowEnum.CHECKOUT && data?.typePickupDisabled}
              dataFlow={dataFlow}
            />
          )}
          {addNewAddressVisibleCheck() && (
            <>
              <CheckoutAddNewAddress
                newAddressType={state.shippingType}
                softFilters={state.softFilters}
                addNewAddressCallback={(deliveryAddressSelected?: (Address & ContactType) | ExtendedContactType) => {
                  if (deliveryAddressSelected && state.shippingType == ShippingTypeEnums.DELIVERY) {
                    saveShippingDetails(deliveryAddressSelected);
                  } else {
                    dispatch(setPickupDrawerVisibility(true));
                  }
                }}
                isLoading={shippingDetailsUpdating}
                ownerId={session?.id}
                addNewDeliveryAddressCtaHandler={() => {
                  dispatchShippingStateAction({
                    type: checkoutShippingDetailsActions.SET_DISPLAY_ADDRESS_BOOK,
                    payload: true,
                  });
                }}
              />
              {getConfirmCtaDisabledState() && confirmButtonCta(true)}
            </>
          )}
          {selectedAddressWithConfirmVisibleCheck() && (
            <>
              <CheckoutSelectedAddressCard
                softFilters={state.softFilters}
                shippingType={state.shippingType}
                selectedStoreOptionName={checkout?.fulfillment?.description?.[0]?.text || ''}
                onEditIconClick={() => {
                  if (state.shippingType == ShippingTypeEnums.DELIVERY) {
                    dispatchShippingStateAction({
                      type: checkoutShippingDetailsActions.SET_DISPLAY_ADDRESS_BOOK,
                      payload: true,
                    });
                  } else {
                    dispatch(setPickupDrawerVisibility(true));
                  }
                }}
                hideEdit={hideSelectedAddressEdit()}
                profileName={getProfileName()}
                selectedAddress={
                  state.shippingType == ShippingTypeEnums.DELIVERY
                    ? transformCheckoutAddressToSelectedAddress(ShippingTypeEnums.DELIVERY, {
                        ...state.deliveryAddress,
                        state: stateTextLocalization(
                          state.deliveryAddress?.state,
                          t,
                          'profile.contactmasterdata.v1.draft.stateCode.',
                        ),
                      })
                    : transformCheckoutAddressToSelectedAddress(ShippingTypeEnums.PICKUP, {
                        ...state.storePickupAddress,
                        imageUrl: getSelectedPickupStoreIcon(),
                      })
                }
              >
                {invalidAddressAlert()}
              </CheckoutSelectedAddressCard>
              {pickupCheckoutLatamFlow && state.shippingType == ShippingTypeEnums.PICKUP && (
                <>
                  {stringBooleanUndefinedChecksOutput(
                    [
                      state.shippingDetailsData?.bopisDetails?.mobileNo,
                      state.shippingDetailsData?.bopisDetails?.name,
                      state.shippingDetailsData?.bopisDetails?.email,
                    ],
                    true,
                  ) ? (
                    <CheckoutSelectedAddressCard
                      selectedAddress={{
                        heading: t('checkout.CheckoutSelectedAddressCard.pickupContact'),
                        title: state.shippingDetailsData?.bopisDetails?.name,
                        recipientContact: `${getValueCheck(
                          state.shippingDetailsData?.bopisDetails?.countryCode,
                          DataTypesEnums.TYPE_STRING,
                        )}${state.shippingDetailsData?.bopisDetails?.mobileNo}`,
                        addressValues: [state.shippingDetailsData?.bopisDetails?.email],
                      }}
                      onEditIconClick={() => {
                        dispatch(setPickupContactDrawerVisibility(true));
                      }}
                    />
                  ) : (
                    <CheckoutAddNewAddress
                      newAddressType={''}
                      addNewAddressCallback={() => {
                        dispatch(setPickupContactDrawerVisibility(true));
                      }}
                      heading={t('checkout.CheckoutAddNewAddress.pickupContact')}
                      ctaText={t('checkout.CheckoutAddNewAddress.pickupContactCta')}
                      isCustomView={true}
                    />
                  )}
                </>
              )}
              {confirmButtonCta(
                getConfirmCtaDisabledState() ||
                  (stringBooleanUndefinedChecksOutput([
                    pickupCheckoutLatamFlow,
                    state.shippingType == ShippingTypeEnums.PICKUP,
                  ]) &&
                    stringBooleanUndefinedChecksOutput(
                      [
                        !state.shippingDetailsData?.bopisDetails?.mobileNo,
                        !state.shippingDetailsData?.bopisDetails?.name,
                        !state.shippingDetailsData?.bopisDetails?.email,
                      ],
                      true,
                    )),
              )}
            </>
          )}
        </>
      )}
      {state.displayConfirmedAddress && !state.displayAddressSelection && (
        <CheckoutConfirmedAddressDetails
          confirmedAddress={
            state.shippingType == ShippingTypeEnums.DELIVERY
              ? {
                  ...transformCheckoutAddressToConfirmedAddress(
                    ShippingTypeEnums.DELIVERY,
                    {
                      ...state.deliveryAddress,
                      state: stateTextLocalization(
                        state.deliveryAddress?.state,
                        t,
                        'profile.contactmasterdata.v1.draft.stateCode.',
                      ),
                    },
                    getProfileName(),
                  ),
                  isSaved: isAddressSavedWithContactId(),
                }
              : transformCheckoutAddressToConfirmedAddress(ShippingTypeEnums.PICKUP, {
                  ...(dataFlow == DataFlowEnum.CHECKOUT && {
                    ...checkout?.fulfillment?.bopisRecipientDetail,
                    ...{ recipientName: checkout?.fulfillment?.bopisRecipientDetail?.name },
                  }),
                  ...(dataFlow == DataFlowEnum.SUBSCRIPTION &&
                    state.shippingDetailsData?.bopisDetails && {
                      ...state.shippingDetailsData?.bopisDetails,
                      ...{ recipientName: state.shippingDetailsData?.bopisDetails?.name },
                    }),
                  imageUrl: getSelectedPickupStoreIcon(),
                  ...state.storePickupAddress,
                })
          }
          shippingType={state.shippingType}
        >
          {invalidAddressAlert()}
        </CheckoutConfirmedAddressDetails>
      )}
      {state.displayAddressBook && (
        <AddressBook
          addressSelected={state.deliveryAddress}
          onConfirmClick={(addressObj: (Address & ContactType) | ExtendedContactType) => {
            saveShippingDetails(addressObj);
          }}
          isLoading={shippingDetailsUpdating}
          useAddressLookup={false}
          onDrawerClose={() =>
            dispatchShippingStateAction({
              type: checkoutShippingDetailsActions.SET_DISPLAY_ADDRESS_BOOK,
              payload: false,
            })
          }
          downlineAddressList={state.shippingDetailsData?.downlineAddressList}
          profileId={session?.id}
          isProxyOrder={
            isProxyNotGroupOrder(checkout) ||
            (dataFlow != DataFlowEnum.CHECKOUT && data?.cartData?.orderType == OrderType.Proxy)
          }
        />
      )}
      {pickupDrawerVisibility && (
        <CheckoutPickupDrawer
          onCloseDrawer={() => {
            dispatch(setPickupDrawerVisibility(false));
            if (state.storePickupAddress && disabledPlaceOrder && checkout?.modification) {
              dispatch(setPlaceOrderDisabled(false));
            }
            dispatch(resetPickupStoreOptions());
          }}
          onConfrimPickupStore={(bopisRecipientDetail, storeOptionName, selectedServiceLocation) => {
            if (!pickupCheckoutLatamFlow) {
              confirmPickupStoreHandler(bopisRecipientDetail, storeOptionName, selectedServiceLocation);
            } else {
              dispatchShippingStateAction({
                type: checkoutShippingDetailsActions.SET_FULFILLMENT_TYPE_FOR_PICKUP,
                payload: storeOptionName,
              });
              dispatchShippingStateAction({
                type: checkoutShippingDetailsActions.STORE_PICKUP_ADDRESS,
                payload: selectedServiceLocation,
              });
              dispatchShippingStateAction({
                type: checkoutShippingDetailsActions.IS_PICKUP_ADDRESS_AVAILABLE,
                payload: true,
              });
              dispatch(setPickupDrawerVisibility(false));
              dispatch(resetPickupStoreOptions());
            }
          }}
          selectedStoreForPickup={state.storePickupAddress}
          bopisDetails={state.shippingDetailsData?.bopisDetails}
          entries={productEntries}
          currencyIsoCode={currencyIsoCode}
          contactFieldEnabled={
            (dataFlow == DataFlowEnum.CHECKOUT && !pickupLatamFlow) || data?.pickupContactFieldEnabled
          }
          confirmButtonLoading={shippingDetailsUpdating}
          locationPermission={state.locationPermission}
          setLocationPermission={(permissionStatus: string) => {
            dispatchShippingStateAction({
              type: checkoutShippingDetailsActions.SET_LOCATION_PERMISSION,
              payload: permissionStatus,
            });
          }}
          showAvailabilityTag={dataFlow == DataFlowEnum.CHECKOUT}
          dataFlow={dataFlow}
        />
      )}
      {pickupCheckoutLatamFlow && pickupContactDrawerVisibility && (
        <CheckoutPickupContactDrawer
          onCloseDrawer={() => {
            dispatch(setPickupContactDrawerVisibility(false));
          }}
          bopisDetails={{ ...checkout?.fulfillment?.bopisRecipientDetail, ...state.shippingDetailsData?.bopisDetails }}
          onConfrimPickupContact={(bopisDetails: BopisRecipientDetail) => {
            confirmPickupStoreHandler(
              bopisDetails,
              state.fulfillmentTypeForpickup || checkout?.fulfillment?.id,
              state.storePickupAddress,
            );
          }}
          confirmButtonLoading={shippingDetailsUpdating}
        />
      )}
    </div>
  );
};

export default memo(CheckoutShippingDetailsContainer);
